// client/src/coms/views/games_parts/GamesHostDialog.js
// Hosting any game from the shared catalogue: who can join (the server the Games screen is open in, your
// friends, or only people with the code or an invitation), the mode (for drawing games) and friends to
// invite. Every game gets the same kind of invitation code.
import React, { useState } from 'react';
import UserAvatar from './UserAvatar';
import { visibilityOptions } from './LobbyParts';
import { playersLabel } from './GamesCatalogue';
import { MODE_ICONS } from '../canvas_parts/party/partyMeta';

export default function GamesHostDialog({ entry, friends = [], server = null, canvasId = 0, busy = false, error = '', onCancel, onCreate }) {
  const serverName = server && server.name ? server.name : '';
  const options = visibilityOptions({ serverName, canvas: Boolean(canvasId) && entry.kind === 'party' });
  const [visibility, setVisibility] = useState(() => (canvasId && entry.kind === 'party' ? 'canvas' : serverName ? 'server' : 'friends'));
  const [preset, setPreset] = useState(entry.modes && entry.modes[0] ? entry.modes[0].id : '');
  const [invited, setInvited] = useState([]);
  const isClassic = entry.kind === 'canvas';
  const inviteLimit = isClassic ? 3 : 40;
  const toggle = (id) => setInvited((current) => (current.includes(id) ? current.filter((x) => x !== id) : current.length >= inviteLimit ? current : [...current, id]));
  const needsInvite = !isClassic && visibility === 'invite' && entry.kind === 'session' && !invited.length;

  return (
    <div className="party-modal-backdrop" role="dialog" aria-modal="true" aria-label={`Host ${entry.name}`} onClick={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <div className="party-modal" style={{ '--party-accent': entry.accent || '#5865f2' }}>
        <div className="party-modal-head">
          <span className="party-card-icon" aria-hidden="true">{entry.kind === 'party' ? MODE_ICONS[entry.icon] || '🎨' : entry.kind === 'canvas' ? '✏️' : entry.arcade && entry.icon ? entry.icon : '🎲'}</span>
          <div>
            <div className="party-kicker">Host a game · {playersLabel(entry)}</div>
            <h3>{entry.name}</h3>
          </div>
        </div>
        {isClassic ? <p className="party-muted">Creates a canvas for the game and opens it in Canvas. Share the invitation code shown in the game panel (up to 4 players), or invite friends here.</p> : null}
        {entry.modes && entry.modes.length ? (
          <div className="party-setting multi">
            <span>Mode</span>
            <div className="party-chips">{entry.modes.map((mode) => <button key={mode.id} type="button" className={`party-chip${preset === mode.id ? ' active' : ''}`} title={mode.description} onClick={() => setPreset(mode.id)}>{mode.label}</button>)}</div>
          </div>
        ) : null}
        {!isClassic ? (
          <label className="party-setting">
            <span>Who can join</span>
            <select value={visibility} onChange={(event) => setVisibility(event.target.value)} data-testid="host-visibility">
              {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        ) : null}
        {friends.length ? (
          <div className="party-setting multi">
            <span>Invite friends {invited.length ? `(${invited.length})` : ''}</span>
            <div className="party-friend-picker">
              {friends.map((friend) => (
                <label key={friend.id} className={invited.includes(friend.id) ? 'active' : ''}>
                  <input type="checkbox" checked={invited.includes(friend.id)} onChange={() => toggle(friend.id)} />
                  <UserAvatar userId={friend.id} name={friend.username} size={22} />
                  {friend.username}
                </label>
              ))}
            </div>
          </div>
        ) : <p className="party-muted">No friends yet: share the invitation code from the lobby instead.</p>}
        {!isClassic ? <p className="party-muted">Everyone gets an invitation code and link in the lobby. {entry.kind === 'party' ? 'You can change every setting there.' : ''}</p> : null}
        {error ? <div role="alert" className="party-error">{error}</div> : null}
        <div className="party-row end">
          <button type="button" className="party-btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="party-btn"
            disabled={busy || needsInvite}
            data-testid="host-create"
            onClick={() => onCreate({ visibility, serverId: visibility === 'server' && server ? Number(server.id) : 0, preset, invitedUserIds: invited })}
          >
            {busy ? 'Creating…' : isClassic ? 'Create canvas game' : 'Create lobby'}
          </button>
        </div>
      </div>
    </div>
  );
}
